import React, { useMemo } from 'react';
import { motion } from 'motion/react';
import { TimeOfDay, WeatherType } from '../types';
import { calculateCelestialState, getDefaultMinuteForPhase } from '../utils/skyCycle';
import { GardenShootingStars } from './GardenShootingStars';
import { GardenClouds } from './GardenClouds';

interface GardenSkyProps {
  timeOfDay: TimeOfDay;
  weather: WeatherType;
  sunPosition?: number;
  minuteOfDay?: number;
  onSunOrMoonClick: (e: React.MouseEvent) => void;
  onCloudClick?: (e: React.MouseEvent) => void;
}

export const GardenSky: React.FC<GardenSkyProps> = React.memo(({
  timeOfDay,
  weather,
  minuteOfDay,
  onSunOrMoonClick,
  onCloudClick,
}) => {
  // Determine the effective minute of the day for calculation
  const currentMinute = useMemo(() => {
    if (minuteOfDay !== undefined) return minuteOfDay;
    return getDefaultMinuteForPhase(timeOfDay);
  }, [minuteOfDay, timeOfDay]);

  // Compute full celestial and atmospheric physics
  const celestial = useMemo(() => {
    return calculateCelestialState(currentMinute, weather);
  }, [currentMinute, weather]);

  // Procedural Starfield with twinkling parameters
  const stars = useMemo(() => {
    return Array.from({ length: 54 }).map((_, i) => ({
      id: i,
      left: 1 + ((i * 19.3) % 98),
      top: 1 + ((i * 13.7) % 56),
      size: 1.0 + ((i * 7) % 18) / 10,
      delay: (i * 0.28) % 3.5,
      duration: 1.8 + ((i * 0.4) % 2.5),
      baseOpacity: 0.35 + ((i * 9) % 65) / 100,
    }));
  }, []);

  const { weights, isSunVisible, isMoonVisible } = celestial;

  return (
    <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none select-none">
      {/* ========================================================================= */}
      {/* 1. CONTINUOUS 4-STAGE INTERPOLATED SKY LAYERS (Identical to para-ti-mvd.com) */}
      {/* ========================================================================= */}

      {/* Layer 1: NOCHE (Deep Navy & Cosmic Violet) */}
      <div
        className="absolute inset-0 transition-opacity duration-300 pointer-events-none"
        style={{
          opacity: weights.noche,
          background:
            'linear-gradient(180deg, #02040b 0%, #060e22 32%, #0e1c3a 62%, #182848 100%)',
        }}
      />

      {/* Layer 2: AMANECER (First light, Soft Lavender, Rose, Apricot & Golden Blush) */}
      <div
        className="absolute inset-0 transition-opacity duration-300 pointer-events-none"
        style={{
          opacity: weights.amanecer,
          background:
            'linear-gradient(180deg, #1b1233 0%, #3e1b3d 24%, #7a2846 48%, #c44e45 70%, #f18d50 86%, #fed082 100%)',
        }}
      />

      {/* Layer 3: DÍA (Radiant Azure Blue with Soft Cyan Atmosphere) */}
      <div
        className="absolute inset-0 transition-opacity duration-300 pointer-events-none"
        style={{
          opacity: weights.dia,
          background:
            'linear-gradient(180deg, #1e70bf 0%, #3895db 30%, #68b8ec 64%, #a8ddf8 86%, #daf2fd 100%)',
        }}
      />

      {/* Layer 4: ATARDECER (Violet Crest, Ruby Twilight, Flaming Amber & Warm Ocre) */}
      <div
        className="absolute inset-0 transition-opacity duration-300 pointer-events-none"
        style={{
          opacity: weights.atardecer,
          background:
            'linear-gradient(180deg, #180a2a 0%, #46143c 22%, #821f42 46%, #c83d2c 68%, #f1762e 85%, #fed274 100%)',
        }}
      />

      {/* Weather Atmospheric Filter (Rain / Clouds overlay) */}
      {weather === 'rainy' && (
        <div
          className="absolute inset-0 pointer-events-none transition-opacity duration-700"
          style={{
            background:
              'linear-gradient(180deg, rgba(16,24,36,0.65) 0%, rgba(28,42,58,0.55) 50%, rgba(48,68,88,0.45) 100%)',
          }}
        />
      )}
      {weather === 'cloudy' && (
        <div
          className="absolute inset-0 pointer-events-none transition-opacity duration-700"
          style={{
            background:
              'linear-gradient(180deg, rgba(65,85,110,0.45) 0%, rgba(95,120,145,0.35) 60%, rgba(135,160,185,0.25) 100%)',
          }}
        />
      )}

      {/* ========================================================================= */}
      {/* 2. AURORA BOREALIS (Glows gently in clear starry nights) */}
      {/* ========================================================================= */}
      <div
        className="absolute top-0 left-0 right-0 h-72 pointer-events-none transition-opacity duration-1000"
        style={{ opacity: celestial.auroraOpacity }}
      >
        <div
          className="w-full h-full animate-pulse"
          style={{
            background:
              'radial-gradient(ellipse 80% 50% at 50% 15%, rgba(64, 224, 180, 0.28) 0%, rgba(72, 180, 224, 0.16) 45%, rgba(147, 51, 234, 0.08) 75%, transparent 100%)',
            filter: 'blur(32px)',
          }}
        />
      </div>

      {/* ========================================================================= */}
      {/* 3. STARRY FIRMAMENT & SHOOTING STARS */}
      {/* ========================================================================= */}
      <div
        className="absolute inset-0 pointer-events-none transition-opacity duration-700"
        style={{ opacity: celestial.starsOpacity }}
      >
        {stars.map((s) => (
          <motion.div
            key={s.id}
            animate={{
              opacity: [s.baseOpacity * 0.4, s.baseOpacity * 1.2, s.baseOpacity * 0.4],
              scale: [0.85, 1.2, 0.85],
            }}
            transition={{
              repeat: Infinity,
              duration: s.duration,
              delay: s.delay,
              ease: 'easeInOut',
            }}
            style={{
              left: `${s.left}%`,
              top: `${s.top}%`,
              width: `${s.size}px`,
              height: `${s.size}px`,
            }}
            className="absolute bg-white rounded-full shadow-[0_0_5px_#fff]"
          />
        ))}
      </div>

      {/* Romantic Meteor Showers & Shooting Stars (Exact para-ti-mvd.com physics & angles) */}
      <GardenShootingStars
        timeOfDay={timeOfDay}
        weather={weather}
        starsOpacity={celestial.starsOpacity}
        nocheWeight={weights.noche}
      />

      {/* ========================================================================= */}
      {/* 4. EXPANDED MULTI-LAYER DRIFTING CLOUDS (12 Realistic Altitudes & Colors) */}
      {/* ========================================================================= */}
      <GardenClouds
        timeOfDay={timeOfDay}
        weather={weather}
        minuteOfDay={currentMinute}
        onCloudClick={onCloudClick}
      />

      {/* ========================================================================= */}
      {/* 5. GOD RAYS / CREPUSCULAR SOLAR LIGHT BEAMS */}
      {/* ========================================================================= */}
      {isSunVisible && weather === 'sunny' && (
        <div
          className="absolute inset-0 pointer-events-none mix-blend-screen transition-opacity duration-700"
          style={{
            opacity: 0.18 * (weights.dia + weights.atardecer * 0.6 + weights.amanecer * 0.6),
            background: `radial-gradient(ellipse at ${celestial.sunX}% ${celestial.sunY}%, rgba(255,253,231,0.85) 0%, rgba(255,224,130,0.3) 45%, transparent 80%)`,
          }}
        />
      )}

      {/* ========================================================================= */}
      {/* 5. CELESTIAL BODY: SUN (With parabolic arc, horizon swell & corona) */}
      {/* ========================================================================= */}
      {isSunVisible && (
        <div
          className="absolute z-10 -translate-x-1/2 -translate-y-1/2 pointer-events-auto cursor-pointer transition-all duration-300 ease-out"
          style={{
            left: `${celestial.sunX}%`,
            top: `${celestial.sunY}%`,
            opacity: celestial.sunVisibleFactor,
            width: `${celestial.sunSize}px`,
            height: `${celestial.sunSize}px`,
          }}
          onClick={onSunOrMoonClick}
          id="garden-celestial-sun"
          title="Sol radiante — Toca para pedir un deseo"
        >
          {/* Broad Sun Ambient Halo */}
          <div
            className="absolute -inset-6 sm:-inset-8 rounded-full pointer-events-none transition-opacity duration-500"
            style={{
              opacity: celestial.sunHaloOpacity,
              background:
                weights.atardecer > 0.4
                  ? 'radial-gradient(circle, rgba(251, 146, 60, 0.7) 0%, rgba(225, 29, 72, 0.3) 45%, transparent 75%)'
                  : weights.amanecer > 0.4
                  ? 'radial-gradient(circle, rgba(253, 186, 116, 0.7) 0%, rgba(244, 63, 94, 0.3) 45%, transparent 75%)'
                  : 'radial-gradient(circle, rgba(254, 240, 138, 0.7) 0%, rgba(245, 158, 11, 0.3) 50%, transparent 75%)',
              filter: 'blur(16px)',
            }}
          />

          {/* Rotating Solar Rays */}
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 45, ease: 'linear' }}
            className="absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-500"
            style={{ opacity: celestial.sunRaysOpacity * 0.7 }}
          >
            {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((deg) => (
              <div
                key={deg}
                className="absolute w-full h-[1.5px] bg-gradient-to-r from-transparent via-amber-200/60 to-transparent blur-[0.5px]"
                style={{ transform: `rotate(${deg}deg)` }}
              />
            ))}
          </motion.div>

          {/* Sun Warmth Layer for Horizon Expansion */}
          <div
            className="absolute inset-0 rounded-full transition-opacity duration-500 pointer-events-none"
            style={{
              opacity: celestial.sunWarmOpacity,
              background:
                'radial-gradient(circle, rgba(255, 120, 40, 0.65) 0%, rgba(255, 70, 10, 0.25) 60%, transparent 85%)',
              filter: 'blur(6px)',
            }}
          />

          {/* Sun Core Orb */}
          <div
            className="relative w-full h-full rounded-full border border-white/90 shadow-2xl transition-all duration-500 overflow-hidden"
            style={{
              background:
                weights.atardecer > 0.4
                  ? 'linear-gradient(135deg, #fff7ed 0%, #fed7aa 25%, #fb923c 60%, #ea580c 100%)'
                  : weights.amanecer > 0.4
                  ? 'linear-gradient(135deg, #fffbeb 0%, #fde68a 25%, #f59e0b 65%, #f43f5e 100%)'
                  : 'linear-gradient(135deg, #ffffff 0%, #fef08a 25%, #fde047 60%, #eab308 100%)',
              boxShadow:
                weights.atardecer > 0.4
                  ? '0 0 35px rgba(234, 88, 12, 0.8), inset 0 0 12px rgba(255, 255, 255, 0.9)'
                  : weights.amanecer > 0.4
                  ? '0 0 30px rgba(245, 158, 11, 0.8), inset 0 0 12px rgba(255, 255, 255, 0.9)'
                  : '0 0 38px rgba(250, 204, 21, 0.85), inset 0 0 12px rgba(255, 255, 255, 0.9)',
            }}
          >
            {/* Top-left Glint */}
            <div className="absolute top-1.5 left-2 w-3.5 h-3.5 bg-white/90 rounded-full blur-[0.8px]" />
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 6. CELESTIAL BODY: MOON (Traversing the night sky with craters & glow) */}
      {/* ========================================================================= */}
      {isMoonVisible && (
        <div
          className="absolute z-10 -translate-x-1/2 -translate-y-1/2 pointer-events-auto cursor-pointer transition-all duration-300 ease-out"
          style={{
            left: `${celestial.moonX}%`,
            top: `${celestial.moonY}%`,
            opacity: celestial.moonVisibleFactor,
          }}
          onClick={onSunOrMoonClick}
          id="garden-celestial-moon"
          title="Luna plateada — Toca para pedir un deseo"
        >
          <div className="relative w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center">
            {/* Lunar Outer Silver Aura */}
            <div className="absolute inset-0 rounded-full bg-slate-200/25 blur-2xl animate-pulse" />
            <div className="absolute -inset-3 rounded-full bg-cyan-300/15 blur-3xl" />

            {/* Moon Orb */}
            <div className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-[#ffffff] via-[#e2e8f0] to-[#94a3b8] shadow-[0_0_35px_rgba(255,255,255,0.75)] border border-white/70 overflow-hidden">
              {/* Moon Craters */}
              <div className="absolute top-2.5 left-3.5 w-2.5 h-2.5 bg-slate-300/60 rounded-full blur-[0.5px] shadow-inner" />
              <div className="absolute top-6 left-2 w-2 h-2 bg-slate-300/50 rounded-full blur-[0.5px]" />
              <div className="absolute top-4 right-3 w-3 h-3 bg-slate-400/40 rounded-full blur-[0.5px] shadow-inner" />
              <div className="absolute bottom-2.5 left-5 w-4 h-3 bg-slate-300/50 rounded-full blur-[0.5px]" />
              <div className="absolute bottom-5 right-2.5 w-2.5 h-2.5 bg-slate-300/60 rounded-full blur-[0.5px]" />

              {/* Crater Shading */}
              <div className="absolute inset-0 bg-radial from-transparent via-slate-400/15 to-slate-500/30 pointer-events-none" />
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 7. HORIZON WARMTH & ROLLING HILLS (Dynamic color grading per phase) */}
      {/* ========================================================================= */}
      <div className="absolute bottom-10 sm:bottom-14 left-0 right-0 z-5 pointer-events-none">
        {/* Warm Horizon Haze at Dawn/Sunset */}
        <div
          className="absolute bottom-0 left-0 right-0 h-44 pointer-events-none transition-opacity duration-500"
          style={{
            opacity: weights.amanecer * 0.85 + weights.atardecer * 0.95,
            background:
              weights.atardecer > weights.amanecer
                ? 'linear-gradient(0deg, rgba(249, 115, 22, 0.45) 0%, rgba(220, 38, 38, 0.22) 50%, transparent 100%)'
                : 'linear-gradient(0deg, rgba(251, 191, 36, 0.42) 0%, rgba(244, 63, 94, 0.2) 50%, transparent 100%)',
            filter: 'blur(20px)',
          }}
        />

        {/* Distant Rolling Hills Silhouette */}
        <svg
          viewBox="0 0 1440 220"
          className="w-full h-20 sm:h-28 md:h-36 transition-colors duration-700"
          preserveAspectRatio="none"
          style={{
            color: `rgba(${Math.round(
              13 * weights.noche + 98 * weights.amanecer + 129 * weights.dia + 106 * weights.atardecer
            )}, ${Math.round(
              29 * weights.noche + 74 * weights.amanecer + 199 * weights.dia + 50 * weights.atardecer
            )}, ${Math.round(
              45 * weights.noche + 104 * weights.amanecer + 132 * weights.dia + 72 * weights.atardecer
            )}, 0.85)`,
          }}
          fill="currentColor"
        >
          <path d="M0,220 L0,90 Q220,10 460,80 Q700,150 940,60 Q1200,-10 1440,70 L1440,220 Z" />
        </svg>

        {/* Mid-distance Rolling Hills */}
        <svg
          viewBox="0 0 1440 180"
          className="absolute bottom-0 left-0 w-full h-16 sm:h-22 md:h-28 transition-colors duration-700"
          preserveAspectRatio="none"
          style={{
            color: `rgba(${Math.round(
              10 * weights.noche + 77 * weights.amanecer + 102 * weights.dia + 77 * weights.atardecer
            )}, ${Math.round(
              23 * weights.noche + 57 * weights.amanecer + 187 * weights.dia + 36 * weights.atardecer
            )}, ${Math.round(
              36 * weights.noche + 84 * weights.amanecer + 106 * weights.dia + 56 * weights.atardecer
            )}, 0.92)`,
          }}
          fill="currentColor"
        >
          <path d="M0,180 L0,80 Q280,120 600,40 Q900,-20 1200,60 Q1340,90 1440,50 L1440,180 Z" />
        </svg>

        {/* Morning & Evening Dew Mist */}
        <div
          className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white/20 via-white/10 to-transparent blur-md transition-opacity duration-700"
          style={{ opacity: celestial.mistOpacity }}
        />
      </div>

      {/* ========================================================================= */}
      {/* 8. VIGNETTE BORDER TINT FOR ATMOSPHERIC DEPTH */}
      {/* ========================================================================= */}
      <div
        className="absolute inset-0 pointer-events-none transition-all duration-700"
        style={{
          boxShadow: `inset 0 0 80px ${celestial.vignette}`,
        }}
      />
    </div>
  );
});

GardenSky.displayName = 'GardenSky';
